@import '../../../styles/variables-common.less';
@import '../../../styles/mixins/fluid-media-queries.mixin.less';

*,
*::after,
*::before {
  box-sizing: border-box;
}

fluid-grid-item {
  position: relative;
  width: 100%; // Default when viewport < 480px (overridden by "col" Prop)
  max-width: 100%;
  flex-basis: 100%;
  flex-grow: 0;
  flex-shrink: 0;
  padding-right: 16px;
  padding-left: 16px;
  box-sizing: border-box;

  // ==== Default Equal-Width Columns (i.e. no "col-..." Props set)

  @media @screen-sm { // viewport size >= 480px
    flex-basis: 0;
    flex-grow: 1;
  }

  // ==== Static Auto Layout (content determines width at ALL breakpoints)

  &.auto { // Must be BEFORE .create-col-sizes() so "col-..." classes can override
    @col-auto();
  }

  // ==== Column Sizes 1-12 & "auto" Size

  .create-col-sizes();

  // ==== Flexbox Order ("order" Prop)

  .create-col-orders();

  // ==== Flexbox Self-Alignments ("alignSelf" Prop)

  .create-self-alignments();

  // ==== "Pull" Column Left/Right

  &.pull-right { margin-left: auto; }
  &.pull-left { margin-right: auto; }

  // ==== Text Alignments ("alignItems" internal Prop)

  &.align-right { text-align: right; }
  &.align-left { text-align: left; }
  &.align-center { text-align: center; }
}

// ================================================================ //
// -- Utility Detached Ruleset(s)

/**
 * Width of a single column (percentage).
 */
@col-basis: round((100 / 12), 6);

/**
 * Styles for "auto" column width.
 */
@col-auto: {
  width: auto;
  max-width: 100%;
  flex-basis: content;
  flex-grow: 0;
  flex-shrink: 0;
}

/**
 * Styles for "fill" column width.
 */
@col-fill: {
  width: 100%;
  max-width: 100%;
  flex-basis: 0;
  flex-grow: 1;
  flex-shrink: 1;
}

// ================================================================ //
// -- Mixins

/**
 * Generates styles for a single column size.
 */
.create-col(@col-size) {
  @basis: round((@col-size * @col-basis), 4);

  width: 100%;
  max-width: ~'@{basis}%';
  flex-basis: ~'@{basis}%';
  flex-grow: 0;
  flex-shrink: 0;
}

/**
 * Creates column sizes 1-12, "auto" size, & "fill" space for each FLUID
 * breakpoint in the format: ".md-1", ".md-2", ".md-auto", etc.
 */
.create-col-sizes() {
  each(@breakpoints, .(@breakpoint, @size) { // Iterate through FLUID breakpoints
    each(range(14), { // Iterate 14 times: 12 for col sizes, 1 for "auto", & 1 for "fill"

      & when (@value < 13) { // Column sizes 1-12
        &.@{size}-@{value} {
          & when (@size = xs) { // Base styles (viewport < 480px)
            .create-col(@value);
          }
          & when not (@size = xs) { // Responsive (viewport >= 480px)
            @media only screen and (min-width: @breakpoint) {
              .create-col(@value);
            }
          }
        }
      }

      & when (@value = 13) { // "auto" size
        &.@{size}-auto {
          & when (@size = xs) { // Base styles (viewport < 480px)
            @col-auto();
          }
          & when not (@size = xs) { // Responsive (viewport >= 480px)
            @media only screen and (min-width: @breakpoint) {
              @col-auto();
            }
          }
        }
      }

      & when (@value = 14) { // "fill" remaining space
        &.@{size}-fill {
          & when (@size = xs) { // Base styles (viewport < 480px)
            @col-fill();
          }
          & when not (@size = xs) { // Responsive (viewport >= 480px)
            @media only screen and (min-width: @breakpoint) {
              @col-fill();
            }
          }
        }
      }
    });
  });
}

/**
 * Creates classes for "order" Prop in the format: ".order-1",
 * ".order-2", etc. Correlates to flexbox "order" property.
 */
.create-col-orders() {
  each(range(12), {
    &.order-@{value} {
      order: @value;
    }
  });
}

/**
 * Creates classes for "alignSelf" Prop in the format:
 * ".align-self-start", ".align-self-center", etc. Correlates
 * to flexbox "align-self" property.
 */
.create-self-alignments() {
  @flex-alignments: {
    start: flex-start;
    center: center;
    end: flex-end;
    stretch: stretch;
    baseline: baseline;
  }

  each(@flex-alignments, {
    &.align-self-@{key} {
      align-self: @value;
    }
  });
}
